import {
  useContext,
  createContext,
  type PropsWithChildren,
  type Dispatch,
  useCallback,
  useMemo,
  useState,
  useEffect,
  type SetStateAction,
} from 'react';
import { createWizardState } from './utilities';
import type {
  CommonVariableDefinitionType,
  Step,
  VariableDefinitionType,
} from 'pages/AutomationEngine/types/automation';
import type {
  AutomationMetaData,
  AutomationVariableDataType,
  DeletedOption,
  GenericOption,
} from 'pages/AutomationEngine/types';
import type {
  SourceVariableType,
  ContextValue,
  VariableNameAndType,
  UiVariable,
  VariableDataTypeOption,
  ArrayAggregationMode,
} from './types';
import variableTypes, { isVariableDefinitionType } from './variable-types';
import type { ValidationKeys } from './variable-types/ValidationKeys';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import { type TFunction, useTranslation } from 'react-i18next';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import {
  getVariables,
  getTriggers,
  getAutomation,
} from 'pages/AutomationEngine/store/selectors';
import { useQuery } from '@tanstack/react-query';
import { CUSTOM_OBJECTS } from '__queries/query-keys';
import { FORCE_ALL_RECORDS_SIZE } from '__services/helpers';
import {
  type CustomObjectMinimal,
  getAllCustomObjects,
} from '@kizen/api/custom-object';
import AxiosService from '__services/AxiosService';
import { useFlashTransition } from '__hooks/useFlashState';
import { translatedMessageDictionary } from 'pages/AutomationEngine/messageDictionary';
import {
  type CheckVariableFromFieldCompatibilityFN,
  type CheckVariableVariableCompatibilityFN,
  defaultDisabledTypes,
  type GetVariableVariableCompatibilityFN,
  useVariableMetaData,
} from './useVariableMetaData';
import { useVariableLogic } from './useVariableLogic';
import { getVariableFormatMetaData } from './variable-types/format-meta-data';
import { useActionOnFailureContext } from '../actionOnFailureContext';
import type { WebhookTriggerData } from '../GoalAndTriggerWizard/subsections/Webhook/types';
import { ArrayAggregationModeOptions } from './constants';

export const defaultContextValue: ContextValue = {
  id: '',
  type: 'initialize_variable',
  variable: {
    name: '',
    dataType: AUTOMATION_VARIABLE_DATA_TYPES.string,
    outputFormat: undefined,
    isArray: false,
    isArrayItemVariable: false,
    sourceArrayVariable: undefined,
  },
  sources: [],
  sourcesMask: [],
  arrayAggregationMode: ArrayAggregationModeOptions.ADD_ALL,
  deduplicateArray: false,
};

const customObjectMapper = (customObjects: CustomObjectMinimal[]) => {
  return customObjects?.length
    ? customObjects.map(
        ({ id, object_name, entity_name }: CustomObjectMinimal) => ({
          value: id,
          label: object_name,
          objectName: object_name,
          entityName: entity_name,
        })
      )
    : [];
};

const getDistinctTriggerActivities = (triggers: any[], triggerType: string[]) =>
  triggers
    .filter(({ type }) => triggerType.includes(type))
    .map((activity) => {
      return { type: activity.type, ...activity.details.activity };
    })
    .filter(
      (opt, index, array) =>
        array.findIndex(({ value }) => value === opt.value) === index
    );

export type DisabledTypes = Record<VariableDefinitionType, boolean>;
export type DisabledDetailsTypes = Record<VariableDefinitionType, string[]>;

export const typeLookUp: Record<VariableDefinitionType, SourceVariableType> = {
  logged_activity_trigger: {
    sourceType: 'logged_activity_trigger',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    loggedActivityVariable: {
      id: '',
      value: undefined,
      variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
      valueCategory: undefined,
      sourceSubtype: undefined,
    },
    id: '',
  },
  activity_past_due_trigger: {
    sourceType: 'activity_past_due_trigger',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    activityPassedDueVariable: {
      id: '',
      value: undefined,
      variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
      valueCategory: undefined,
      sourceSubtype: undefined,
    },
    id: '',
  },
  static: {
    sourceType: 'static',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    staticVariable: {
      id: '',
      value: '',
      valueHtml: '',
      variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
      valueCategory: undefined,
    },
    id: '',
  },
  field_value: {
    sourceType: 'field_value',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    fieldVariable: {
      id: '',
      value: '',
      variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
      valueCategory: undefined,
      sourceSubtype: undefined,
      field: undefined,
      relationshipField: undefined,
    },
    id: '',
  },
  trigger_time: {
    sourceType: 'trigger_time',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    id: '',
  },
  webhook_trigger: {
    sourceType: 'webhook_trigger',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    webhookVariable: {
      id: '',
      value: undefined,
      variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
      valueCategory: undefined,
    },
    id: '',
  },
};

const getTypeLookUp = (type: CommonVariableDefinitionType) =>
  isVariableDefinitionType(type) ? typeLookUp[type] : undefined;

const labelLookUp = (t: TFunction): Record<VariableDefinitionType, string> => ({
  logged_activity_trigger: t('Logged Activity Value'),
  activity_past_due_trigger: t('Activity Past Due Trigger'),
  static: t('Static  Value'),
  field_value: t('Field Value'),
  trigger_time: t('Current Date/Time'),
  webhook_trigger: t('Webhook Data'),
});

export const iconBySourceType: Record<CommonVariableDefinitionType, string> = {
  logged_activity_trigger: 'clipboard-check-light',
  activity_past_due_trigger: 'bell-exclamation',
  static: 'input-text',
  field_value: 'id-card',
  trigger_time: 'clock',
  variable: 'brackets-curly',
  webhook_trigger: 'webhook',
};

export type VariableErrorType = Record<ValidationKeys, string[]>;
export type VariableErrorTypes = Record<string, VariableErrorType>;
type ValidateType = {
  showMessage: boolean;
  message: string;
} | null;

export type ActivityLoggedContext = {
  activities: (GenericOption & { type: string })[];
};

export type ActivityPastDueContext = {
  activities: (GenericOption & { type: string })[];
};

export type WebhookTriggerContext = {
  webhooks: (WebhookTriggerData & { id: string })[];
};

export type VariableCommonContext = {
  id: string | null;
  variable: VariableNameAndType;
  arrayAggregationMode: ArrayAggregationMode;
  deduplicateArray: boolean;
  nameError: string | null;
  setNameError: Dispatch<SetStateAction<string | null>>;
  setVariableName: (name: string) => void;
  setVariableDataType: ({ label, value }: VariableDataTypeOption) => void;
  setVariableDataSubtype: ({ label, value }: DeletedOption) => void;
  setVariableOutputFormat: ({ label, value }: GenericOption) => void;
  setVariableIsArray: (isArray: boolean) => void;
  setArrayAggregationMode: (arrayAggregationMode: ArrayAggregationMode) => void;
  setDeduplicateArray: (deduplicateArray: boolean) => void;
  values: any;
  setValues: Dispatch<any>;
  isValid: boolean;
  checkActive: () => boolean;
  sources: SourceVariableType[];
  addVariableSource: ({
    value,
  }: {
    value: CommonVariableDefinitionType;
  }) => void;
  sourcesMask: VariableDefinitionType[];
  disabledTypes: DisabledTypes;
  moveVariableSource: (fromIndex: number, offSet: number) => void;
  deleteVariableSource: (index: number) => void;
  staticIndex: number;
  updateVariableSource: (update: SourceVariableType) => void;
  errors: VariableErrorTypes;
  setErrors: Dispatch<VariableErrorTypes>;
  clickValidate: () => { isValid: boolean };

  uniqueNames: string[];
  businessObjectOptions: GenericOption[];
  activityLoggedContext: ActivityLoggedContext;
  activityPastDueContext: ActivityPastDueContext;
  webhookTriggerContext: WebhookTriggerContext;
  metaData: AutomationMetaData;
  checkVariableFromFieldCompatibility: CheckVariableFromFieldCompatibilityFN;
  checkVariableVariableCompatibility: CheckVariableVariableCompatibilityFN;
  getVariableVariableCompatibility: GetVariableVariableCompatibilityFN;
  showWarning: string | boolean;
  onHideWarningConfirmed: () => void;
  onHideWarningCanceled: () => void;
  validateDataSubtype: ValidateType;
  showOutputFormat: boolean;
  outputFormatOptions: GenericOption[] | undefined;
};

export type VariableDescriptionContext = VariableCommonContext & {
  getSourceLabel: (type: CommonVariableDefinitionType) => string;
};

type VariableDescriptionProviderProps = {
  step?: Step;
};

export const context = createContext<VariableDescriptionContext>({
  id: '',
  variable: defaultContextValue.variable,
  arrayAggregationMode: defaultContextValue.arrayAggregationMode,
  deduplicateArray: defaultContextValue.deduplicateArray,
  nameError: null,
  setNameError: () => {},
  setVariableName: () => {},
  setVariableDataType: () => {},
  setVariableDataSubtype: () => {},
  setVariableOutputFormat: () => {},
  setVariableIsArray: () => {},
  setArrayAggregationMode: () => {},
  setDeduplicateArray: () => {},
  values: {},
  setValues: () => {},
  isValid: false,
  checkActive: () => false,
  sources: [],
  sourcesMask: [],
  disabledTypes: defaultDisabledTypes,
  addVariableSource: () => {},
  moveVariableSource: () => {},
  deleteVariableSource: () => {},
  staticIndex: -1,
  updateVariableSource: () => {},
  errors: {},
  setErrors: () => {},
  clickValidate: () => ({ isValid: false }),
  getSourceLabel: () => '',
  uniqueNames: [],
  businessObjectOptions: [],
  activityLoggedContext: { activities: [] },
  activityPastDueContext: { activities: [] },
  webhookTriggerContext: { webhooks: [] },
  metaData: {
    detailed_log_templates: {},
  },
  checkVariableFromFieldCompatibility: () => false,
  checkVariableVariableCompatibility: () => false,
  getVariableVariableCompatibility: () => ({}),
  showWarning: false,
  onHideWarningConfirmed: () => {},
  onHideWarningCanceled: () => {},
  validateDataSubtype: null,
  showOutputFormat: false,
  outputFormatOptions: undefined,
});

export const useVariableDescriptionContext = () => useContext(context);

export const VariableDescriptionProvider = ({
  children,
  step,
}: PropsWithChildren<VariableDescriptionProviderProps>) => {
  const { t } = useTranslation();
  const automation = useAutomationsSelector(getAutomation);
  const [objectMessage, showObjectMessage, flashObjectErrorMessage] =
    useFlashTransition();

  const translatedLabels = useMemo(() => labelLookUp(t), [t]);

  const [values, setValues] = useState<ContextValue>(() =>
    createWizardState(step, defaultContextValue)
  );

  const { considerEmptyAnError } = useActionOnFailureContext();

  const [validateOnLoad, setValidateOnLoad] = useState(false);

  const [errors, setErrors] = useState<VariableErrorTypes>({});
  const [nameError, setNameError] = useState<string | null>(
    step?.stepError?.variable?.name?.[0] || null
  );

  const {
    id,
    variable,
    sources,
    sourcesMask,
    arrayAggregationMode,
    deduplicateArray,
  } = values || {};

  const variables: UiVariable[] = useAutomationsSelector(getVariables);
  const triggers: any[] = useAutomationsSelector(getTriggers);
  const [showWarning, setShowWarning] = useState<boolean | string>(false);
  const [nextDataType, setNextDataType] =
    useState<AutomationVariableDataType>('string');
  const [incompatibleSources, setIncompatibleSources] = useState<
    SourceVariableType['id'][]
  >([]);

  const [showOutputFormat, setShowOutputFormat] = useState<boolean>(false);
  const [outputFormatOptions, setOutputFormatOptions] = useState<
    GenericOption[] | undefined
  >(undefined);

  const activityLoggedContext = useMemo(() => {
    return {
      activities: getDistinctTriggerActivities(triggers, ['activity_logged']),
    };
  }, [triggers]);

  const activityPastDueContext = useMemo(() => {
    return {
      activities: getDistinctTriggerActivities(triggers, [
        'scheduled_activity_overdue',
      ]),
    };
  }, [triggers]);

  const webhookTriggerContext = useMemo(() => {
    return {
      webhooks: triggers
        .filter(({ type }) => type === 'webhook')
        .map((wh) => ({
          id: wh.id,
          ...(wh.details as WebhookTriggerData),
        })),
    };
  }, [triggers]);

  const uniqueNames = useMemo(() => {
    return variables
      .filter((v) => v.type === 'initialize_variable' && v.id !== id)
      .map((v) => v.details.variable?.name?.toLowerCase());
  }, [variables, id]);

  const setVariableSources = useCallback(
    (sources: SourceVariableType[]) =>
      setValues((prev: ContextValue) => ({ ...prev, sources })),
    [setValues]
  );

  const setVariableName = useCallback(
    (name: string) =>
      setValues((prev: ContextValue) => ({
        ...prev,
        variable: {
          ...prev.variable,
          name: name.length <= 100 ? name : variable.name,
        },
      })),

    [setValues, variable]
  );

  const { data: businessObjectOptions = [] } = useQuery({
    queryKey: [...CUSTOM_OBJECTS.LIST, FORCE_ALL_RECORDS_SIZE],
    queryFn: () => {
      return getAllCustomObjects(AxiosService);
    },
    refetchOnWindowFocus: false,
    select: customObjectMapper,
  });

  const setVariableDataSubtype = useCallback((dataSubtype: DeletedOption) => {
    setValues((prev: ContextValue) => {
      const nextSources = prev.sources.map((source) => {
        if (source.sourceType === 'static') {
          const { staticVariable } = source;
          return {
            ...source,
            staticVariable: {
              ...staticVariable!,
              value: undefined,
              valueCategory: dataSubtype,
            },
          };
        }
        return source;
      });

      return {
        ...prev,
        variable: {
          ...prev.variable,
          dataSubtype,
        },
        sources: nextSources,
      };
    });
  }, []);

  const setVariableOutputFormat = useCallback((outputFormat: GenericOption) => {
    setValues((prev: ContextValue) => ({
      ...prev,
      variable: {
        ...prev.variable,
        outputFormat: outputFormat?.value || undefined,
      },
    }));
  }, []);

  const setVariableIsArray = useCallback((isArray: boolean) => {
    setValues((prev: ContextValue) => {
      const nextSources = prev.sources.map((source) => {
        if (source.sourceType === 'static') {
          const { staticVariable } = source;
          return {
            ...source,
            staticVariable: {
              ...staticVariable!,
              value: undefined,
            },
          };
        }
        return source;
      });

      return {
        ...prev,
        sources: nextSources,
        variable: {
          ...prev.variable,
          isArray,
        },
      };
    });
  }, []);

  const setArrayAggregationMode = useCallback(
    (arrayAggregationMode: ArrayAggregationMode) => {
      setValues((prev: ContextValue) => ({
        ...prev,
        arrayAggregationMode,
        deduplicateArray: false,
      }));
    },
    []
  );

  const setDeduplicateArray = useCallback((deduplicateArray: boolean) => {
    setValues((prev: ContextValue) => ({
      ...prev,
      deduplicateArray,
    }));
  }, []);

  const {
    metaData,
    checkVariableFromFieldCompatibility,
    getDisabledTypes,
    getCompatibleSources,
  } = useVariableMetaData(typeLookUp, true);

  const setVariableDataType = useCallback(
    ({ value: dataType }: VariableDataTypeOption) => {
      const compatibleSources = getCompatibleSources({
        dataType,
        isArray: false,
        name: '',
        outputFormat: undefined,
        isArrayItemVariable: false,
        sourceArrayVariable: undefined,
      });

      const nextIncompatibleSources = sources
        .filter((source) => {
          if (source.sourceType === 'logged_activity_trigger') {
            return (
              source.loggedActivityVariable?.sourceSubtype &&
              !compatibleSources.logged_activity_trigger.includes(
                source.loggedActivityVariable?.sourceSubtype!
              )
            );
          }

          if (source.sourceType === 'activity_past_due_trigger') {
            return (
              source.activityPassedDueVariable?.sourceSubtype &&
              !compatibleSources.activity_past_due_trigger.includes(
                source.activityPassedDueVariable?.sourceSubtype!
              )
            );
          }

          if (source.sourceType === 'trigger_time') {
            return !compatibleSources.trigger_time.includes(dataType);
          }

          return false;
        })
        .map(({ id }) => id);

      setIncompatibleSources(nextIncompatibleSources);

      if (nextIncompatibleSources.length) {
        setNextDataType(dataType);
        setShowWarning(
          t(
            'This change will result in {{nextIncompatibleSources}} incompatible value sources to be deleted. Are you sure?',
            { nextIncompatibleSources: nextIncompatibleSources.length }
          )
        );
        return;
      }

      const outputMetaData = getVariableFormatMetaData(dataType);

      setValues((prev: ContextValue) => {
        const nextSources = prev.sources.map((source) => {
          if (source.sourceType === 'static') {
            const { staticVariable } = source;
            return {
              ...source,
              sourceSubtype: dataType, // keep this in synch for now going fwd may have it's own selector
              staticVariable: {
                ...staticVariable!,
                value:
                  dataType === AUTOMATION_VARIABLE_DATA_TYPES.boolean
                    ? 'True'
                    : '',
                valueCategory: undefined,
              },
            };
          }
          return source;
        });
        return {
          ...prev,
          variable: {
            ...prev.variable,
            dataType,
            dataSubtype: undefined,
            outputFormat: outputMetaData
              ? outputMetaData?.meta?.default_output_format
              : undefined,
            isArray: false,
          },
          sources: nextSources,
        };
      });
    },

    [getCompatibleSources, sources, t]
  );

  useEffect(() => {
    const outputMetaData = getVariableFormatMetaData(variable?.dataType);
    setShowOutputFormat(!!outputMetaData);
    setOutputFormatOptions(
      outputMetaData ? outputMetaData?.meta?.output_format : undefined
    );
  }, [variable?.dataType]);

  const onHideWarningConfirmed = useCallback(() => {
    setValues((prev: ContextValue) => {
      const nextSources = prev.sources
        .filter(({ id }) => !incompatibleSources.includes(id))
        .map((source) => {
          if (source.sourceType === 'static') {
            const { staticVariable } = source;
            return {
              ...source,
              sourceSubtype: nextDataType, // keep this in synch for now going fwd may have it's own selector
              staticVariable: {
                ...staticVariable,
                value:
                  nextDataType === AUTOMATION_VARIABLE_DATA_TYPES.boolean
                    ? 'True'
                    : '',
                valueCategory: undefined,
              },
            } as SourceVariableType;
          }
          return source;
        });

      return {
        ...prev,
        variable: {
          ...prev.variable,
          dataType: nextDataType,
          dataSubtype: undefined,
          isArray: false,
        },
        sources: nextSources,
      };
    });

    // remove any with an error
    const nextErrors = incompatibleSources.reduce((currentErrors, id) => {
      const { [id]: _, ...nextErrors } = currentErrors;
      return nextErrors;
    }, errors);

    setErrors(nextErrors);

    setShowWarning(false);
  }, [incompatibleSources, errors, nextDataType]);

  const onHideWarningCanceled = useCallback(() => {
    setShowWarning(false);
  }, [setShowWarning]);

  const staticIndex = useMemo(() => {
    return (sources || []).findIndex(
      ({ sourceType }) => sourceType === 'static'
    );
  }, [sources]);

  const {
    addVariableSource,
    moveVariableSource,
    deleteVariableSource,
    updateVariableSource,
  } = useVariableLogic(
    variable,
    staticIndex,
    sources,
    setVariableSources,
    true,
    errors,
    setErrors,
    getTypeLookUp
  );

  const isValid = Boolean(
    (sources?.length ||
      (variable.isArray && !considerEmptyAnError) ||
      !considerEmptyAnError) &&
      !nameError &&
      (variable.dataType !== AUTOMATION_VARIABLE_DATA_TYPES.entity ||
        variable.dataSubtype) &&
      !Object.keys(errors || {}).length
  );

  const clickValidate = (): { isValid: boolean } => {
    let isValid = !!variable.name;
    const newErrors = sources.reduce((acc, variableSource) => {
      let errs = {};
      const validate =
        variableTypes?.[variableSource.sourceType as VariableDefinitionType]
          ?.validate;
      if (validate) {
        const { valid, errors } = validate({
          values: variableSource,
          checkRequired: true,
          t,
        });
        if (!valid) {
          isValid = false;
        }

        errs = errors.reduce((currentErrors: any, err: any) => {
          return { ...currentErrors, ...err };
        }, {});
      }
      if (Object.keys(errs).length) {
        return { ...acc, [variableSource.id]: { ...errs } };
      }
      return acc;
    }, {});
    if (!nameError && !variable.name) {
      setNameError(t('This field may not be blank.'));
    }
    setErrors(newErrors);
    return { isValid };
  };

  useEffect(() => {
    if (sources && !validateOnLoad) {
      const newErrors = sources.reduce((acc, variableSource) => {
        let errs = {};
        const validate =
          variableTypes?.[variableSource.sourceType as VariableDefinitionType]
            ?.validate;
        if (validate) {
          const { errors } = validate({
            values: variableSource,
            checkRequired: false,
            t,
          });

          errs = errors.reduce((currentErrors: any, err: any) => {
            return { ...currentErrors, ...err };
          }, {});
        }
        if (Object.keys(errs).length) {
          return { ...acc, [variableSource.id]: { ...errs } };
        }
        return acc;
      }, {});
      setErrors(newErrors);

      if (variable.dataSubtype?.deleted) {
        flashObjectErrorMessage(
          translatedMessageDictionary(t, automation.type === 'global')
            .objectHasBeenDeleted
        );
      }

      setValidateOnLoad(true);
    }
  }, [
    flashObjectErrorMessage,
    sources,
    t,
    validateOnLoad,
    variable?.dataSubtype?.deleted,
    automation.type,
  ]);

  const validateDataSubtype: ValidateType = useMemo(
    () =>
      showObjectMessage
        ? { showMessage: showObjectMessage, message: objectMessage }
        : null,
    [showObjectMessage, objectMessage]
  );

  const getSourceLabel = useCallback(
    (type: CommonVariableDefinitionType) =>
      isVariableDefinitionType(type) ? translatedLabels[type] : t('Unknown'),
    [translatedLabels, t]
  );

  const disabledTypes: DisabledTypes = useMemo(
    () => getDisabledTypes(variable),
    [getDisabledTypes, variable]
  );

  const ctx = {
    id,
    variable,
    arrayAggregationMode,
    deduplicateArray,
    nameError,
    setNameError,
    setVariableName,
    setVariableDataType,
    setVariableDataSubtype,
    setVariableOutputFormat,
    setVariableIsArray,
    setArrayAggregationMode,
    setDeduplicateArray,

    values,
    setValues,
    isValid,
    checkActive: () => Boolean(sources?.length) || variable?.isArray,
    sources,
    addVariableSource,
    sourcesMask,
    disabledTypes,
    moveVariableSource,
    deleteVariableSource,
    updateVariableSource,
    staticIndex,
    errors,
    setErrors,
    clickValidate,
    getSourceLabel,
    uniqueNames,
    businessObjectOptions,
    activityLoggedContext,
    activityPastDueContext,
    webhookTriggerContext,
    metaData,
    checkVariableFromFieldCompatibility,
    checkVariableVariableCompatibility: () => false,
    getVariableVariableCompatibility: () => ({}),
    showWarning,
    onHideWarningConfirmed,
    onHideWarningCanceled,
    validateDataSubtype,
    showOutputFormat,
    outputFormatOptions,
  };
  return <context.Provider value={ctx}>{children}</context.Provider>;
};
